import { Injectable } from '@angular/core';

import { SlipState } from '@bpos/v1/my-bets';
import { AccountBalanceService } from '@frontend/sports/account-balance/feature';
import { MyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { LocalStoreService } from '@frontend/vanilla/core';

import { MyBetsBetSlip } from '../../my-bets-base/models/my-bets-viewmodels';
import { IMyBetsListRequest } from './my-bets-api.service';

@Injectable()
export class SettledBalanceService {
    private static lastSettledBetStorageKey = 'LastSettled';
    private static trackingPageName = 'myBets_Balance_Refreshed';
    private static trackingSection = 'myBets';
    private readonly logger: SportsRemoteLogger;

    constructor(
        private accountBalanceService: AccountBalanceService,
        private storage: LocalStoreService,
        private myBetsConfig: MyBetsConfig,
        loggerFactory: LoggerFactory,
        private trackingService: TrackingService,
    ) {
        this.logger = loggerFactory.getLogger('SettledBalanceService');
    }

    processResult(request: IMyBetsListRequest, listType: BetslipListType | undefined, bets: MyBetsBetSlip[]): void {
        const lastBet = this.getFirstEntry(bets);

        if (
            !lastBet ||
            !this.myBetsConfig.enableBalanceRefresh ||
            !this.isUnfilteredSettledBetsRequest(request, listType) ||
            !this.betHasChanged(lastBet)
        ) {
            return;
        }

        if (!this.containsBetsAffectingBalance(bets)) {
            this.setLastSettledBetSlipId(lastBet.betslipId);

            return;
        }

        this.accountBalanceService.refresh$(false).subscribe({
            next: () => {
                this.setLastSettledBetSlipId(lastBet.betslipId);
                this.trackBalanceUpdate();
            },
            error: (err) => {
                this.logger.error(err, 'Failled updating user balance for my-bets scope.');
            },
        });
    }

    private isUnfilteredSettledBetsRequest(request: IMyBetsListRequest, listType: BetslipListType | undefined): boolean {
        return (
            listType === BetslipListType.Settled &&
            request.index === 1 &&
            !request.fromDate &&
            !request.toDate &&
            !request.outcome &&
            !request.eventIds?.length &&
            !request.pinnedBetslipIds
        );
    }

    private getFirstEntry(bets: MyBetsBetSlip[]): MyBetsBetSlip | null {
        return bets && bets.length ? bets[0] : null;
    }

    private betHasChanged(bet: MyBetsBetSlip): boolean {
        const localSettledBetslipId = this.getLocalSettledBetSlipId();

        return bet.betslipId !== localSettledBetslipId;
    }

    private containsBetsAffectingBalance(bets: MyBetsBetSlip[]): boolean {
        return bets && bets.some((bet) => bet.state !== SlipState.Lost);
    }

    private getLocalSettledBetSlipId(): string {
        return this.storage.get(SettledBalanceService.lastSettledBetStorageKey) as string;
    }

    private setLastSettledBetSlipId(betslipId: string): void {
        this.storage.set(SettledBalanceService.lastSettledBetStorageKey, betslipId);
    }

    private trackBalanceUpdate(): void {
        this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, {
            [trackingConstants.PAGE_NAME]: SettledBalanceService.trackingPageName,
            [trackingConstants.PAGE_SITE_SECTION]: SettledBalanceService.trackingSection,
        });
    }
}
